import { useEffect, useState, type ReactNode } from "react";
import { AtlLogo } from "../../components/atelier";
import { Ico } from "../../components/shell";
import { useAuth } from "../../auth";
import { api, ApiError } from "../../api";
import { navigate } from "../../router";
// Full-bleed bakery hero for the brand panel. Bundled + swappable: drop a
// hi-res internal-database shot in over this one file to re-skin the login.
import loginHero from "../../assets/login-hero.jpg";

/** Whether the very first (admin) account still needs creating. */
function useBootstrap(): boolean | null {
  const [needs, setNeeds] = useState<boolean | null>(null);
  useEffect(() => {
    api
      .bootstrapStatus()
      .then((r) => setNeeds(r.needsBootstrap))
      .catch(() => setNeeds(false));
  }, []);
  return needs;
}

/* ── Shared split-screen shell: bake-photo brand panel + form card ── */
function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="atl" style={{ display: "flex" }}>
      {/* Brand panel: full-bleed bake photo + claret wash (hidden on narrow screens) */}
      <div className="atl-auth-brand">
        <img className="atl-auth-photo" src={loginHero} alt="" aria-hidden="true" />
        <div className="atl-auth-scrim" />
        <div style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "44px 46px", color: "#fff" }}>
          <AtlLogo light />
          <h1 className="atl-serif" style={{ fontSize: 44, lineHeight: 1.04, maxWidth: 440 }}>
            Time to crown a <span style={{ fontStyle: "italic" }}>breadhead.</span>
          </h1>
        </div>
      </div>

      {/* Form side */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 30, minWidth: 0 }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          {/* Brand mark above the form only when the photo panel is hidden (mobile) */}
          <div className="atl-auth-formlogo"><AtlLogo /></div>
          {children}
        </div>
      </div>

      <style>{`
        .atl-auth-brand { flex: 0 0 46%; max-width: 620px; position: relative; overflow: hidden; background: #2a1418; }
        .atl-auth-photo { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .atl-auth-scrim { position: absolute; inset: 0; z-index: 1;
          background:
            linear-gradient(102deg, rgba(111,35,48,.88) 0%, rgba(111,35,48,.46) 45%, rgba(40,12,16,.34) 100%),
            linear-gradient(0deg, rgba(26,8,10,.82) 0%, rgba(26,8,10,0) 52%); }
        .atl-auth-formlogo { display: none; justify-content: center; margin-bottom: 24px; }
        @media (max-width: 860px) {
          .atl-auth-brand { display: none; }
          .atl-auth-formlogo { display: flex; }
        }
      `}</style>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="atl-eyebrow" style={{ marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

function ErrorChip({ msg }: { msg: string }) {
  return (
    <div className="atl-chip atl-chip--accent" style={{ justifyContent: "center", padding: "9px 13px", fontWeight: 600 }}>
      {msg}
    </div>
  );
}

export function Login() {
  const auth = useAuth();
  const needsBootstrap = useBootstrap();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy || !username.trim() || !password) return;
    setBusy(true);
    setErr("");
    try {
      await auth.login(username.trim(), password);
      navigate("/");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <div className="atl-card" style={{ padding: "32px 30px 28px" }}>
        <h2 className="atl-serif" style={{ fontSize: 28, textAlign: "center" }}>
          Right, let's get <span style={{ fontStyle: "italic", color: "var(--atl-accent)" }}>baking.</span>
        </h2>
        <p style={{ color: "var(--atl-soft)", fontSize: 14, fontWeight: 500, textAlign: "center", marginTop: 6 }}>
          Sign in to host quizzes and prep your bakes.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 26 }}>
          <Field label="USERNAME">
            <input
              className="atl-tf"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </Field>
          <Field label="PASSWORD">
            <input
              className="atl-tf"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </Field>
          {err && <ErrorChip msg={err} />}
          <button className="atl-btn atl-btn--primary atl-btn--lg atl-btn--block" disabled={busy} onClick={submit}>
            {busy ? "Lighting the oven…" : "Sign in"} <Ico name="play" size={14} />
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 18, fontSize: 13, color: "var(--atl-soft)", fontWeight: 500 }}>
          {needsBootstrap ? (
            <>
              First time here?{" "}
              <span className="atl-link2" onClick={() => navigate("/signup")}>
                Create the admin account
              </span>
            </>
          ) : (
            "Accounts are created by an admin."
          )}
        </div>
      </div>
      <PlayerLink />
    </AuthShell>
  );
}

export function Signup() {
  const auth = useAuth();
  const needsBootstrap = useBootstrap();
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy || !username.trim() || !password) return;
    setBusy(true);
    setErr("");
    try {
      await auth.signup(username.trim(), password, displayName.trim() || username.trim());
      navigate("/");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Something went wrong");
      setBusy(false);
    }
  }

  if (needsBootstrap === false) {
    return (
      <AuthShell>
        <div className="atl-card" style={{ padding: "32px 30px 28px", textAlign: "center" }}>
          <h2 className="atl-serif" style={{ fontSize: 26 }}>
            Signups are <span style={{ fontStyle: "italic", color: "var(--atl-accent)" }}>closed.</span>
          </h2>
          <p style={{ color: "var(--atl-soft)", fontSize: 14, fontWeight: 500, marginTop: 8, lineHeight: 1.5 }}>
            Bakeoff accounts are created by an admin. Ask your admin to set you up, then sign in.
          </p>
          <button className="atl-btn atl-btn--primary atl-btn--lg atl-btn--block" style={{ marginTop: 20 }} onClick={() => navigate("/login")}>
            Back to sign in
          </button>
        </div>
        <PlayerLink />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="atl-card" style={{ padding: "32px 30px 28px" }}>
        <h2 className="atl-serif" style={{ fontSize: 28, textAlign: "center" }}>
          Join the <span style={{ fontStyle: "italic", color: "var(--atl-accent)" }}>bakery.</span>
        </h2>
        <p style={{ color: "var(--atl-soft)", fontSize: 14, fontWeight: 500, textAlign: "center", marginTop: 6 }}>
          Create a host account — we'll seed your first quiz to get you rising.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 24 }}>
          <Field label="YOUR NAME">
            <input
              className="atl-tf"
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Priya K."
            />
          </Field>
          <Field label="USERNAME">
            <input
              className="atl-tf"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
              placeholder="Username"
            />
          </Field>
          <Field label="PASSWORD">
            <input
              className="atl-tf"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </Field>
          {err && <ErrorChip msg={err} />}
          <button className="atl-btn atl-btn--primary atl-btn--lg atl-btn--block" disabled={busy} onClick={submit}>
            {busy ? "Prepping your station…" : "Create account"}
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 18, fontSize: 13, color: "var(--atl-soft)", fontWeight: 500 }}>
          Already baking?{" "}
          <span className="atl-link2" onClick={() => navigate("/login")}>
            Sign in
          </span>
        </div>
      </div>
      <PlayerLink />
    </AuthShell>
  );
}

function PlayerLink() {
  return (
    <div style={{ textAlign: "center", marginTop: 16, fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 600 }}>
      Here to play?{" "}
      <span className="atl-link2" style={{ color: "var(--atl-soft)", textDecoration: "underline", textUnderlineOffset: 3 }} onClick={() => navigate("/play")}>
        Join a game with a PIN →
      </span>
    </div>
  );
}
